import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { agentsApi } from '@/api/agents'
import { useTenantKey } from './useTenantKey'
import { declaresProjectMemory } from '@/lib/agentYaml'
import type {
  AgentDetail,
  CreateAgentRequest,
  UpdateAgentRequest,
  CreateAgentVersionRequest,
  CreateAgentMemoryEntryRequest,
  UpdateAgentMemoryEntryRequest,
} from '@/types'

export function useAgents(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'agents'),
    queryFn: () => agentsApi.getAll(projectId),
    enabled: !!projectId,
  })
}

export function useAgent(projectId: string, agentId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'agents', agentId ?? ''),
    queryFn: () => agentsApi.getById(projectId, agentId!),
    enabled: !!projectId && !!agentId,
  })
}

export function useAgentVersions(projectId: string, agentId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'agents', agentId ?? '', 'versions'),
    queryFn: () => agentsApi.getVersions(projectId, agentId!),
    enabled: !!projectId && !!agentId,
  })
}

export function useAgentMemory(projectId: string, agentId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'agents', agentId ?? '', 'memory'),
    queryFn: () => agentsApi.getMemory(projectId, agentId!),
    enabled: !!projectId && !!agentId,
  })
}

export function useCreateAgent(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (data: CreateAgentRequest) => agentsApi.create(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'agents') })
    },
  })
}

export function useUpdateAgent(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAgentRequest }) =>
      agentsApi.update(projectId, id, data),
    onSuccess: (_result, { id, data }) => {
      queryClient.setQueryData<AgentDetail>(
        tenantKey('projects', projectId, 'agents', id),
        previous => previous && { ...previous, displayName: data.displayName, description: data.description ?? null },
      )
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'agents') })
    },
  })
}

export function useDeleteAgent(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (id: string) => agentsApi.delete(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'agents') })
    },
  })
}

export function useDuplicateAgent(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (id: string) => agentsApi.duplicate(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'agents')})
    },
  })
}

export function useCreateAgentVersion(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateAgentVersionRequest }) =>
      agentsApi.createVersion(projectId, id, data),
    onSuccess: (result, { id, data }) => {
      queryClient.setQueryData<AgentDetail>(
        tenantKey('projects', projectId, 'agents', id),
        previous => previous && {
          ...previous,
          yamlContent: data.yaml,
          memoryEnabled: declaresProjectMemory(data.yaml),
          latestVersion: {
            id: result.agentVersionId,
            agentId: id,
            version: result.version,
            yamlSnapshot: data.yaml,
            contentHash: previous.latestVersion?.contentHash ?? '',
            createdAt: new Date().toISOString(),
            createdByName: previous.latestVersion?.createdByName ?? null,
          },
        },
      )
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'agents') })
    },
  })
}

export function useCreateAgentMemoryEntry(projectId: string, agentId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (data: CreateAgentMemoryEntryRequest) => agentsApi.createMemoryEntry(projectId, agentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'agents', agentId, 'memory') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    },
  })
}

export function useSaveAgentMemoryEntries(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: async ({ agentId, entries }: { agentId: string; entries: (CreateAgentMemoryEntryRequest & { key: string })[] }) => {
      const failedKeys: string[] = []
      let firstError: unknown = null
      for (const { key, type, content } of entries) {
        try {
          await agentsApi.createMemoryEntry(projectId, agentId, { type, content })
        } catch (error) {
          failedKeys.push(key)
          firstError ??= error
        }
      }
      return { failedKeys, firstError }
    },
    onSuccess: (_result, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'agents', agentId, 'memory') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    },
  })
}

export function useUpdateAgentMemoryEntry(projectId: string, agentId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ entryId, data }: { entryId: string; data: UpdateAgentMemoryEntryRequest }) =>
      agentsApi.updateMemoryEntry(projectId, agentId, entryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'agents', agentId, 'memory') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    },
  })
}

export function useDeleteAgentMemoryEntry(projectId: string, agentId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (entryId: string) => agentsApi.deleteMemoryEntry(projectId, agentId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'agents', agentId, 'memory') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
    },
  })
}
